import dayjs from "dayjs";
import { PackageTariffTypes } from "@Shared/Entities/Supplier/InstantPackage/enums";
export const PLAN_LABELS = {
    [PackageTariffTypes.Individual]: "Individual",
    [PackageTariffTypes.Aggregated]: "Aggregated",
    [PackageTariffTypes.Pooled]: "Pooled",
    [PackageTariffTypes.IndividualPlus]: "Individual+",
};
export const mapStatus = (value) => {
    if (value === "contacted" ||
        value === "contractPending" ||
        value === "contractComplete") {
        return value;
    }
    return "contacted";
};
export const getParameters = (raw) => {
    const parameters = raw.parameters;
    return parameters && typeof parameters === "object"
        ? parameters
        : undefined;
};
export const formatPlanLabel = (parameters) => {
    if (!parameters) {
        return "—";
    }
    if (typeof parameters.plan === "string" && parameters.plan.trim()) {
        return parameters.plan;
    }
    const tariffOption = parameters.tariffOption ?? "";
    return PLAN_LABELS[tariffOption] ?? tariffOption;
};
export const isSharedPlan = (tariffOption) => tariffOption === PackageTariffTypes.Aggregated ||
    tariffOption === PackageTariffTypes.Pooled ||
    tariffOption === "aggregated" ||
    tariffOption === "pooled";
export const formatAllowanceValue = (value) => {
    if (value === "Unlimited" || String(value).toLowerCase() === "unlimited") {
        return "Unlimited";
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${numeric}GB` : String(value);
};
export const formatDataAllowance = (raw, parameters) => {
    const lockedLines = raw.lines;
    if (Array.isArray(lockedLines) && lockedLines.length > 0) {
        const values = lockedLines
            .map((line) => line.allowances?.data?.value)
            .filter((value) => value !== undefined);
        if (values.length > 0) {
            const uniqueValues = [...new Set(values.map(String))];
            if (uniqueValues.length === 1) {
                return formatAllowanceValue(values[0]);
            }
        }
    }
    const dataGb = parameters?.requiredData ?? parameters?.dataDetails?.totalRequiredData;
    return dataGb === undefined ? "—" : `${dataGb}GB`;
};
export const formatDisplayDataAllowance = (raw, parameters) => {
    const dataAllowance = formatDataAllowance(raw, parameters);
    if (isSharedPlan(parameters?.tariffOption) &&
        dataAllowance !== "Unlimited" &&
        dataAllowance !== "—" &&
        !dataAllowance.includes("Shared")) {
        return `${dataAllowance} Shared`;
    }
    return dataAllowance;
};
export const formatDealSummaryFromObject = (summary) => {
    const lines = summary.lines ?? 0;
    const lineLabel = lines === 1 ? "line" : "lines";
    const dataType = summary.dataType
        ? `${summary.dataType.charAt(0).toUpperCase()}${summary.dataType.slice(1)}`
        : "—";
    return `${lines} ${lineLabel} · ${dataType}`;
};
export const formatDealSummary = (raw) => {
    const parameters = getParameters(raw);
    const lineCount = parameters?.lines ?? 0;
    const lineLabel = lineCount === 1 ? "line" : "lines";
    return `${lineCount} ${lineLabel} · ${formatDataAllowance(raw, parameters)} · ${formatPlanLabel(parameters)}`;
};
export const getDealId = (raw) => {
    if (typeof raw.id === "string" && raw.id.trim()) {
        return raw.id;
    }
    if (typeof raw.instantPackageId === "string" &&
        raw.instantPackageId.trim()) {
        return raw.instantPackageId;
    }
    if (typeof raw.customerUserId === "string" && raw.customerUserId.trim()) {
        return raw.customerUserId;
    }
    if (typeof raw.customerId === "string" && raw.customerId.trim()) {
        return raw.customerId;
    }
    return null;
};
export const getSupplierName = (raw) => {
    if (typeof raw.supplierName === "string" && raw.supplierName.trim()) {
        return raw.supplierName;
    }
    const supplier = raw.supplier;
    if (supplier &&
        typeof supplier === "object" &&
        typeof supplier.name === "string") {
        return supplier.name;
    }
    return "—";
};
export const getSupplierEmail = (raw) => {
    if (typeof raw.supplierContact === "string") {
        return raw.supplierContact;
    }
    if (typeof raw.supplierEmail === "string") {
        return raw.supplierEmail;
    }
    const supplierUser = raw.supplierUser;
    if (supplierUser &&
        typeof supplierUser === "object" &&
        typeof supplierUser.email === "string") {
        return supplierUser.email;
    }
    return "";
};
export const getSupplierPhone = (raw) => {
    if (typeof raw.supplierPhone === "string") {
        return raw.supplierPhone;
    }
    const supplierUser = raw.supplierUser;
    if (supplierUser &&
        typeof supplierUser === "object" &&
        typeof supplierUser.mobileNumber ===
            "string") {
        return supplierUser.mobileNumber;
    }
    return "—";
};
export const formatAllowanceLimit = (value) => {
    if (value === undefined || value === null || value === "") {
        return "Unlimited";
    }
    if (value === "Unlimited" ||
        value === -1 ||
        String(value).toLowerCase() === "unlimited") {
        return "Unlimited";
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? String(numeric) : "Unlimited";
};
// const formatDealSummaryDataTotal = (
//     dataTotal: string | number | undefined
// ): string => {
//     if (dataTotal === undefined || dataTotal === null || dataTotal === "") {
//         return "—"
//     }
//     return typeof dataTotal === "number" ? `${dataTotal}GB` : dataTotal
// }
export const formatCustomerDealSummaryPrimary = (raw) => {
    const summary = raw.dealSummary;
    if (summary && typeof summary === "object") {
        const { lines = 0, 
        // dataTotal,
        contractLength, } = summary;
        const lineLabel = lines === 1 ? "line" : "lines";
        const months = typeof contractLength === "number" && contractLength > 0
            ? `${contractLength} months`
            : "—";
        return `${months} · ${lines} ${lineLabel}`;
    }
    const parameters = getParameters(raw);
    const contractLength = raw.contractLength ?? parameters?.contractLength;
    const lineCount = parameters?.lines ?? 0;
    const lineLabel = lineCount === 1 ? "line" : "lines";
    const dataAllowance = formatDisplayDataAllowance(raw, parameters);
    const months = typeof contractLength === "number" && contractLength > 0
        ? `${contractLength} months`
        : "—";
    return `${months} · ${lineCount} ${lineLabel} · ${dataAllowance}`;
};
export const formatCustomerDealSummarySecondary = (raw) => {
    const lockedLines = getLockedLines(raw);
    const firstLine = lockedLines[0];
    const calls = formatAllowanceLimit(firstLine?.allowances?.callLimit);
    const texts = formatAllowanceLimit(firstLine?.allowances?.smsLimit);
    return `${calls} calls · ${texts} texts`;
};
export const getMonthlyCost = (raw) => {
    const cost = raw.monthlyTotalCost ?? raw.monthlyCost;
    if (typeof cost === "number" && Number.isFinite(cost)) {
        return cost;
    }
    return null;
};
export const formatDealTime = (date) => {
    const hours = `${date.getHours()}`.padStart(2, "0");
    const minutes = `${date.getMinutes()}`.padStart(2, "0");
    return `${hours}:${minutes}`;
};
export const parseDealDate = (raw) => {
    for (const key of ["date", "createdAt", "updatedAt"]) {
        const value = raw[key];
        if (typeof value === "string" || typeof value === "number") {
            const parsed = dayjs(value);
            if (parsed.isValid()) {
                return parsed.toDate();
            }
        }
        if (value &&
            typeof value === "object" &&
            typeof value.toISOString ===
                "function") {
            const parsed = dayjs(value.toISOString());
            if (parsed.isValid()) {
                return parsed.toDate();
            }
        }
    }
    return new Date();
};
export const getCustomerName = (raw) => {
    if (typeof raw.customerName === "string" && raw.customerName.trim()) {
        return raw.customerName;
    }
    const customer = raw.customer;
    if (customer &&
        typeof customer === "object" &&
        typeof customer.name === "string") {
        return customer.name;
    }
    const customerUser = raw.customerUser;
    if (customerUser &&
        typeof customerUser === "object" &&
        typeof customerUser.name === "string") {
        return customerUser.name;
    }
    return "—";
};
export const getCustomerEmail = (raw) => {
    if (typeof raw.customerContact === "string") {
        return raw.customerContact;
    }
    if (typeof raw.customerEmail === "string") {
        return raw.customerEmail;
    }
    const customerUser = raw.customerUser;
    if (customerUser &&
        typeof customerUser === "object" &&
        typeof customerUser.email === "string") {
        return customerUser.email;
    }
    return "";
};
export const getCustomerPhone = (raw) => {
    if (typeof raw.customerPhone === "string") {
        return raw.customerPhone;
    }
    const customerUser = raw.customerUser;
    if (customerUser &&
        typeof customerUser === "object" &&
        typeof customerUser.mobileNumber ===
            "string") {
        return customerUser.mobileNumber;
    }
    return "—";
};
export const formatMoney = (amount) => `£${amount.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
})}`;
export const formatDealDate = (date) => {
    const day = `${date.getDate()}`.padStart(2, "0");
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};
export const formatSubmittedDate = formatDealDate;
export const formatSubmittedDateTime = (date) => {
    const hours = `${date.getHours()}`.padStart(2, "0");
    const minutes = `${date.getMinutes()}`.padStart(2, "0");
    return `${formatDealDate(date)} ${hours}:${minutes}`;
};
export const parsePhoneLevelSplit = (phoneLevelSplit) => {
    if (!phoneLevelSplit) {
        return { low: 0, mid: 0, high: 0 };
    }
    if (typeof phoneLevelSplit === "object") {
        return {
            low: phoneLevelSplit.low ?? 0,
            mid: phoneLevelSplit.mid ?? 0,
            high: phoneLevelSplit.high ?? 0,
        };
    }
    try {
        const parsed = JSON.parse(phoneLevelSplit);
        return {
            low: parsed.low ?? 0,
            mid: parsed.mid ?? 0,
            high: parsed.high ?? 0,
        };
    }
    catch {
        return { low: 0, mid: 0, high: 0 };
    }
};
export const getLockedLines = (raw) => Array.isArray(raw.lines) ? raw.lines : [];
export const getCommonCharges = (raw, parameters) => {
    const rootCharges = raw.commonCharges;
    if (rootCharges && typeof rootCharges === "object") {
        return rootCharges;
    }
    return parameters?.commonCharges ?? {};
};
export const getRoaming = (raw, parameters) => {
    if (raw.roaming && typeof raw.roaming === "object") {
        return raw.roaming;
    }
    return parameters?.roaming;
};
export const getCustomerService = (raw, parameters) => {
    if (raw.customerService && typeof raw.customerService === "object") {
        return raw.customerService;
    }
    return parameters?.customerService;
};
